

import { useState, useMemo, useCallback } from "react";
import { cn, ErrorBanner } from "@fmc-digital/ui";
import { useNavigate } from "react-router-dom";
import { StepperProcess } from "./StepperProcess";
import { SecurityBadge } from "./SecurityBadge";
import { RegisterContext, RegisterStep, EmailSubstep, PersonalInfoSubstep, PhoneSubstep } from "./RegisterContext";
import { EmailStep } from "./steps/EmailStep";
import { OtpStep } from "./steps/OtpStep";
import { PersonalInfoStep } from "./steps/PersonalInfoStep";
import { PasswordStep } from "./steps/PasswordStep";
import { PhoneStep } from "./steps/PhoneStep";
import { PhoneOtpStep } from "./steps/PhoneOtpStep";
import { PhoneConsentStep } from "./steps/PhoneConsentStep";
import {
  registerFlowRootStyles,
  registerStepperWrapperStyles,
  registerStepCardStyles,
  registerOtpHeaderStyles,
  registerErrorBannerStyles,
  registerSecurityBadgeStyles,
} from "./register-flow.styles";

import { RenderStepHeader, maskPhoneForOtpHeader } from "@/components/molecules/RegisterStepHeader/stepHeaders";
import { ROUTES } from "@/constants/routes.constants";
import {
  addEmailLineBreakOpportunity,
  processEmailForDisplay,
} from "@/utils/mfa-formatting";

interface RegisterStepCardProps {
  step: RegisterStep;
  emailSubstep: EmailSubstep;
  personalInfoSubstep: PersonalInfoSubstep;
  phoneSubstep: PhoneSubstep;
  setStatusMessage: (msg: string | null) => void;
  enableResendOnOtpLoad: boolean;
  onConsumeEnableResendOnOtpLoad: () => void;
}

function RegisterStepCard({
  step,
  emailSubstep,
  personalInfoSubstep,
  phoneSubstep,
  setStatusMessage,
  enableResendOnOtpLoad,
  onConsumeEnableResendOnOtpLoad,
}: RegisterStepCardProps & { email?: string }) {
  return (
    <div
      key={`register-card-${step}-${emailSubstep}-${personalInfoSubstep}-${phoneSubstep}`}
      className={cn(registerStepCardStyles())}
    >
      {step === "email" && (emailSubstep === "input"
        ? <EmailStep setStatusMessage={setStatusMessage} />
        : (
            <OtpStep
              setStatusMessage={setStatusMessage}
              enableResendOnLoad={enableResendOnOtpLoad}
              onConsumeEnableResendOnLoad={onConsumeEnableResendOnOtpLoad}
            />
          ))}
      {step === "personalInfo" && (personalInfoSubstep === "input"
        ? <PersonalInfoStep setStatusMessage={setStatusMessage} />
        : <PasswordStep setStatusMessage={setStatusMessage} />)}
      {step === "phone" && phoneSubstep === "input" && <PhoneStep setStatusMessage={setStatusMessage} />}
      {step === "phone" && phoneSubstep === "otp" && <PhoneOtpStep setStatusMessage={setStatusMessage} />}
      {step === "phone" && phoneSubstep === "consent" && <PhoneConsentStep setStatusMessage={setStatusMessage} />}
      <div className={cn(registerSecurityBadgeStyles())}>
        <SecurityBadge />
      </div>
    </div>
  );
}



export function RegisterStepper() {
  const navigate = useNavigate();
  const [step, setStep] = useState<RegisterStep>("email");
  const [emailSubstep, setEmailSubstep] = useState<EmailSubstep>("input");
  const [personalInfoSubstep, setPersonalInfoSubstep] = useState<PersonalInfoSubstep>("input");
  const [phoneSubstep, setPhoneSubstep] = useState<PhoneSubstep>("input");

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [personalInfo, setPersonalInfo] = useState(
    { lastName: "", loanNumber: "", ssn: "" },
  );
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpId, setOtpId] = useState("");
  const [otpFactorId, setOtpFactorId] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneFactorId, setPhoneFactorId] = useState("");
  const [phoneConsent, setPhoneConsent] = useState(false);
  const [enableResendOnOtpLoad, setEnableResendOnOtpLoad] = useState(false);

  // Error/success message state
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const flowSteps: RegisterStep[] = ["email", "personalInfo", "phone"];
  const activeStep = Math.max(0, flowSteps.indexOf(step));

  const canGoBack = !(
    step === "phone" && (phoneSubstep === "input" || phoneSubstep === "consent")
  );

  // Navigation logic
  const goNext = () => {
    if (step === "email") {
      if (emailSubstep === "input") {
        setEnableResendOnOtpLoad(false);
        setEmailSubstep("otp");
      } else if (emailSubstep === "otp") {
        setStep("personalInfo");
        setEmailSubstep("input");
      }
    } else if (step === "personalInfo") {
      if (personalInfoSubstep === "input") {
        setPersonalInfoSubstep("password");
      } else if (personalInfoSubstep === "password") {
        setStep("phone");
        setPersonalInfoSubstep("input");
        setPhoneSubstep("input");
      }
    } else if (step === "phone") {
      if (phoneSubstep === "input") {
        setPhoneSubstep("otp");
      } else if (phoneSubstep === "otp") {
        setPhoneSubstep("consent");
      } else {
        // Registration complete: navigate to success page
        navigate(ROUTES.REGISTER_FEEDBACK, email ? { state: { username: email } } : undefined);
      }
    }
  };

  // Custom back button logic for each step/substep
  const handleBack = useCallback(() => {
    if (step === "email" && emailSubstep === "input") {
      // Email input step: back to value proposition/landing
      navigate("/register");
      return;
    }
    if (step === "email" && emailSubstep === "otp") {
      // OTP step: back to email input, preserve email
      setEnableResendOnOtpLoad(false);
      setEmailSubstep("input");
      setStatusMessage(null);
      return;
    }
    if (step === "personalInfo" && personalInfoSubstep === "input") {
      // Personal info input: back to email input, preserve email
      setStep("email");
      setEmailSubstep("input");
      setStatusMessage(null);
      return;
    }
    if (step === "personalInfo" && personalInfoSubstep === "password") {
      // Password step: back to personal info input
      setPersonalInfoSubstep("input");
      setStatusMessage(null);
      return;
    }
    if (step === "phone" && phoneSubstep === "input") {
      setStep("personalInfo");
      setPersonalInfoSubstep("password");
      setStatusMessage(null);
      return;
    }
    if (step === "phone" && phoneSubstep === "otp") {
      setPhoneSubstep("input");
      setStatusMessage(null);
      return;
    }
    // Default fallback
    navigate("/register");
  }, [
    step,
    emailSubstep,
    personalInfoSubstep,
    phoneSubstep,
    navigate,
    setEmailSubstep,
    setPersonalInfoSubstep,
    setPhoneSubstep,
    setStep,
  ]);

  const contextValue = useMemo(() => ({
    step,
    setStep,
    emailSubstep,
    setEmailSubstep,
    personalInfoSubstep,
    setPersonalInfoSubstep,
    phoneSubstep,
    setPhoneSubstep,
    email,
    setEmail,
    otp,
    setOtp,
    personalInfo,
    setPersonalInfo,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    otpId,
    setOtpId,
    otpFactorId,
    setOtpFactorId,
    phone,
    setPhone,
    phoneFactorId,
    setPhoneFactorId,
    phoneConsent,
    setPhoneConsent,
    goNext,
    goBack: handleBack,
  }), [
    step,
    setStep,
    emailSubstep,
    setEmailSubstep,
    personalInfoSubstep,
    setPersonalInfoSubstep,
    phoneSubstep,
    setPhoneSubstep,
    email,
    setEmail,
    otp,
    setOtp,
    personalInfo,
    setPersonalInfo,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    otpId,
    setOtpId,
    otpFactorId,
    setOtpFactorId,
    phone,
    setPhone,
    phoneFactorId,
    setPhoneFactorId,
    phoneConsent,
    setPhoneConsent,
    goNext,
    handleBack,
  ]);
  return (
    <RegisterContext.Provider value={contextValue}>
      <div className={cn(registerFlowRootStyles())} data-testid="register-flow-root">
        <div className={cn(registerStepperWrapperStyles())} data-testid="register-stepper-wrapper">
          <StepperProcess currentStep={activeStep} onBack={canGoBack ? handleBack : undefined} />
        </div>
        {/* Step header: skip for OTP substeps, show for all others */}
        {(step !== "email" || emailSubstep !== "otp")
          && (step !== "phone" || phoneSubstep !== "otp") && (
          <RenderStepHeader
            step={step}
            emailSubstep={emailSubstep}
            personalInfoSubstep={personalInfoSubstep}
            phoneSubstep={phoneSubstep}
            email={email}
            phone={phone}
          />
        )}
        {/* OTP custom header above card */}
        {step === "email" && emailSubstep === "otp" && (
          <div
            className={cn(registerOtpHeaderStyles())}
            data-testid="otp-step-header"
          >
            <span className="mfa-destination-prefix">Enter the 6-digit code we sent to </span>
            <span className="mfa-destination-email">
              [{addEmailLineBreakOpportunity(processEmailForDisplay(email))}]
            </span>
          </div>
        )}
        {step === "phone" && phoneSubstep === "otp" && (
          <div
            className={cn(registerOtpHeaderStyles())}
            data-testid="phone-otp-step-header"
          >
            <span className="mfa-destination-prefix">Enter the 6-digit code we sent to </span>
            <span className="mfa-destination-nowrap">[{maskPhoneForOtpHeader(phone)}]</span>
          </div>
        )}
        <RegisterStepCard
          step={step}
          emailSubstep={emailSubstep}
          personalInfoSubstep={personalInfoSubstep}
          phoneSubstep={phoneSubstep}
          setStatusMessage={setStatusMessage}
          enableResendOnOtpLoad={enableResendOnOtpLoad}
          onConsumeEnableResendOnOtpLoad={() => setEnableResendOnOtpLoad(false)}
          email={email}
        />
        {/* Error Banner at the bottom of all step cards */}
        {statusMessage && (
          <div className={cn(registerErrorBannerStyles())}>
            <ErrorBanner
              message={statusMessage}
              testID="register-error-banner"
              className="[&_span]:text-[14px] [&_span]:leading-5"
            />
          </div>
        )}
        {/* Spacer between card and footer */}
        <div style={{ height: 48 }} />
      </div>
    </RegisterContext.Provider>
  );
}
